import React, { useState, useEffect } from 'react'
import { 
  CurrencyDollarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  CalendarIcon,
  ArrowDownTrayIcon,
  ChartPieIcon,
  BanknotesIcon,
  CreditCardIcon
} from '@heroicons/react/24/outline'

interface IngresoMensual {
  mes: string
  ingresos: number
  gastos: number
  utilidad: number
}

interface GastoCategoria {
  categoria: string
  monto: number
  porcentaje: number
  color: string
}

interface MetricaFinanciera {
  label: string
  valor: string
  cambio: number
  icon: React.ComponentType<any>
  color: string
}

const PaginaReporteFinanciero: React.FC = () => {
  const [fechaInicio, setFechaInicio] = useState('')
  const [fechaFin, setFechaFin] = useState('')
  const [tipoReporte, setTipoReporte] = useState<'mensual' | 'trimestral' | 'anual'>('mensual')
  const [ingresosMensuales, setIngresosMensuales] = useState<IngresoMensual[]>([])
  const [gastosCategoria, setGastosCategoria] = useState<GastoCategoria[]>([])

  useEffect(() => {
    // Datos de ejemplo
    const ingresosMensualesEjemplo: IngresoMensual[] = [
      { mes: 'Enero', ingresos: 45000000, gastos: 32000000, utilidad: 13000000 },
      { mes: 'Febrero', ingresos: 48000000, gastos: 34000000, utilidad: 14000000 },
      { mes: 'Marzo', ingresos: 52000000, gastos: 36000000, utilidad: 16000000 },
      { mes: 'Abril', ingresos: 49000000, gastos: 35000000, utilidad: 14000000 },
      { mes: 'Mayo', ingresos: 55000000, gastos: 38000000, utilidad: 17000000 },
      { mes: 'Junio', ingresos: 58000000, gastos: 40000000, utilidad: 18000000 }
    ]

    const gastosCategoriaEjemplo: GastoCategoria[] = [
      { categoria: 'Combustible', monto: 15000000, porcentaje: 37.5, color: 'bg-red-500' },
      { categoria: 'Mantenimiento', monto: 8000000, porcentaje: 20, color: 'bg-blue-500' },
      { categoria: 'Salarios', monto: 12000000, porcentaje: 30, color: 'bg-green-500' },
      { categoria: 'Seguros', monto: 3000000, porcentaje: 7.5, color: 'bg-yellow-500' },
      { categoria: 'Otros', monto: 2000000, porcentaje: 5, color: 'bg-purple-500' }
    ]

    setIngresosMensuales(ingresosMensualesEjemplo)
    setGastosCategoria(gastosCategoriaEjemplo)

    // Establecer fechas por defecto (últimos 6 meses)
    const hoy = new Date()
    const hace6Meses = new Date(hoy.getFullYear(), hoy.getMonth() - 6, 1)
    setFechaFin(hoy.toISOString().split('T')[0])
    setFechaInicio(hace6Meses.toISOString().split('T')[0])
  }, [])

  const calcularMetricasFinancieras = (): MetricaFinanciera[] => {
    const totalIngresos = ingresosMensuales.reduce((sum, item) => sum + item.ingresos, 0)
    const totalGastos = ingresosMensuales.reduce((sum, item) => sum + item.gastos, 0)
    const totalUtilidad = ingresosMensuales.reduce((sum, item) => sum + item.utilidad, 0)
    const margenUtilidad = totalIngresos > 0 ? (totalUtilidad / totalIngresos) * 100 : 0

    return [
      {
        label: 'Ingresos Totales',
        valor: `$${(totalIngresos / 1000000).toFixed(1)}M`,
        cambio: 12.5,
        icon: CurrencyDollarIcon,
        color: 'green'
      },
      {
        label: 'Gastos Totales',
        valor: `$${(totalGastos / 1000000).toFixed(1)}M`,
        cambio: 8.3,
        icon: CreditCardIcon,
        color: 'red'
      },
      {
        label: 'Utilidad Neta',
        valor: `$${(totalUtilidad / 1000000).toFixed(1)}M`,
        cambio: 18.7,
        icon: BanknotesIcon,
        color: 'blue'
      },
      {
        label: 'Margen de Utilidad',
        valor: `${margenUtilidad.toFixed(1)}%`,
        cambio: 4.2,
        icon: ChartPieIcon,
        color: 'purple'
      }
    ]
  }

  const metricasFinancieras = calcularMetricasFinancieras()

  const getColorClasses = (color: string) => {
    const colors = {
      green: 'bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-400',
      red: 'bg-red-100 dark:bg-red-900 text-red-600 dark:text-red-400',
      blue: 'bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400',
      purple: 'bg-purple-100 dark:bg-purple-900 text-purple-600 dark:text-purple-400'
    }
    return colors[color as keyof typeof colors] || colors.blue
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount)
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Reporte Financiero</h1>
            <p className="text-gray-600 dark:text-gray-400">Análisis financiero y de rentabilidad</p>
          </div>
          <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors">
            <ArrowDownTrayIcon className="h-5 w-5" />
            <span>Exportar Reporte</span>
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="mb-6 bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Fecha Inicio
            </label>
            <div className="relative">
              <CalendarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="date"
                value={fechaInicio}
                onChange={(e) => setFechaInicio(e.target.value)}
                className="pl-10 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Fecha Fin
            </label>
            <div className="relative">
              <CalendarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="date"
                value={fechaFin}
                onChange={(e) => setFechaFin(e.target.value)}
                className="pl-10 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Tipo de Reporte
            </label>
            <select
              value={tipoReporte}
              onChange={(e) => setTipoReporte(e.target.value as 'mensual' | 'trimestral' | 'anual')}
              className="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
            >
              <option value="mensual">Mensual</option>
              <option value="trimestral">Trimestral</option>
              <option value="anual">Anual</option>
            </select>
          </div>

          <div className="flex items-end">
            <button className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors">
              Generar Reporte
            </button>
          </div>
        </div>
      </div>

      {/* Métricas financieras */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        {metricasFinancieras.map((metrica, index) => (
          <div key={index} className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
            <div className="flex items-center">
              <div className={`p-2 rounded-lg ${getColorClasses(metrica.color)}`}>
                <metrica.icon className="h-6 w-6" />
              </div>
              <div className="ml-4 flex-1">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{metrica.label}</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{metrica.valor}</p>
                <div className="flex items-center mt-1">
                  {metrica.cambio >= 0 ? (
                    <ArrowTrendingUpIcon className="h-4 w-4 text-green-500 mr-1" />
                  ) : (
                    <ArrowTrendingDownIcon className="h-4 w-4 text-red-500 mr-1" />
                  )}
                  <span className={`text-sm ${metrica.cambio >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {metrica.cambio >= 0 ? '+' : ''}{metrica.cambio}%
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">vs período anterior</span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Ingresos vs Gastos por mes */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
            Ingresos vs Gastos Mensuales
          </h3>
          <div className="space-y-4">
            {ingresosMensuales.map((item, index) => (
              <div key={index} className="border-b border-gray-200 dark:border-gray-700 pb-4 last:border-b-0">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium text-gray-900 dark:text-white">{item.mes}</span>
                  <span className={`text-sm font-medium ${item.utilidad >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(item.utilidad)}
                  </span>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Ingresos</span>
                    <span className="text-green-600 font-medium">{formatCurrency(item.ingresos)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Gastos</span>
                    <span className="text-red-600 font-medium">{formatCurrency(item.gastos)}</span>
                  </div>
                </div>
                <div className="mt-2 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div 
                    className="bg-green-500 h-2 rounded-full" 
                    style={{ width: `${(item.utilidad / item.ingresos) * 100}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Distribución de gastos */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
            Distribución de Gastos
          </h3>
          <div className="space-y-4">
            {gastosCategoria.map((gasto, index) => (
              <div key={index} className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className={`w-4 h-4 rounded ${gasto.color}`}></div>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {gasto.categoria}
                  </span>
                </div>
                <div className="text-right">
                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                    {formatCurrency(gasto.monto)}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {gasto.porcentaje}%
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          <div className="mt-6">
            <div className="flex rounded-full h-3 overflow-hidden">
              {gastosCategoria.map((gasto, index) => (
                <div
                  key={index}
                  className={gasto.color}
                  style={{ width: `${gasto.porcentaje}%` }}
                ></div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Tabla detallada */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
            Detalle Financiero Mensual
          </h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500 dark:text-gray-400">
            Análisis detallado de ingresos, gastos y utilidades por período
          </p>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Período
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Ingresos
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Gastos
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Utilidad
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Margen
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {ingresosMensuales.map((item, index) => {
                const margen = (item.utilidad / item.ingresos) * 100
                return (
                  <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {item.mes}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-green-600 font-medium">
                        {formatCurrency(item.ingresos)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-red-600 font-medium">
                        {formatCurrency(item.gastos)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className={`text-sm font-medium ${item.utilidad >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(item.utilidad)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className={`text-sm font-medium ${margen >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {margen.toFixed(1)}%
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

export default PaginaReporteFinanciero